import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

export default function RequireAdmin({ children }: { children: ReactNode }) {
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;

    const doCheck = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!data?.session) {
          if (!cancelled) {
            setIsAdmin(false);
            setChecking(false);
          }
          return;
        }

        const { data: adminData, error: rpcError } = await supabase.rpc('is_admin_mgr');
        if (rpcError) {
          if (!cancelled) {
            setError('Lỗi kiểm tra quyền admin: ' + rpcError.message);
            setIsAdmin(false);
            setChecking(false);
          }
          return;
        }

        if (!cancelled) {
          setIsAdmin(!!adminData);
          setChecking(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Lỗi không xác định');
          setIsAdmin(false);
          setChecking(false);
        }
      }
    };

    doCheck();

    return () => {
      cancelled = true;
    };
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="flex items-center gap-3 text-foreground-600">
          <i className="ri-loader-4-line text-2xl animate-spin text-primary-400" />
          <span className="text-sm">Đang tải...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 px-4">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 rounded-full bg-rose-500/15 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-rose-400 text-2xl" />
          </div>
          <h2 className="text-lg font-bold text-foreground-950 mb-2">Không thể truy cập</h2>
          <p className="text-sm text-foreground-600 mb-4">{error}</p>
          <a
            href={`/dang-nhap?next=${encodeURIComponent(location.pathname)}`}
            className="inline-block px-4 py-2 rounded-md bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-600 cursor-pointer whitespace-nowrap transition-colors"
          >
            Đăng nhập lại
          </a>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to={`/dang-nhap?next=${encodeURIComponent(location.pathname)}`} replace />;
  }

  return <>{children}</>;
}